import React, { useState, useEffect } from 'react';
import { 
  User, Clock, Calendar, HardDrive, FileText, 
  Download, Loader2, Mail, Phone, MapPin, Briefcase, AlertCircle, Info, FileCheck, LayoutTemplate 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { employeeService } from '../../services/employeeService';
import { payrollService } from '../../services/payrollService';
import { leaveService } from '../../services/leaveService';
import StatusBadge from '../../components/StatusBadge';

// SRS 6.5: Centralized Tabs including My Documents
const TABS = [
  { id: 'profile', label: 'My Profile', icon: <User className="w-4 h-4" /> },
  { id: 'attendance', label: 'Attendance', icon: <Clock className="w-4 h-4" /> },
  { id: 'leaves', label: 'Leave Balance', icon: <Calendar className="w-4 h-4" /> },
  { id: 'assets', label: 'Assigned Assets', icon: <HardDrive className="w-4 h-4" /> },
  { id: 'payroll', label: 'My Payslips', icon: <FileText className="w-4 h-4" /> },
  { id: 'documents', label: 'My Documents', icon: <FileCheck className="w-4 h-4" /> }
];

const SelfService = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false); // State for download process
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    profile: null,
    attendance: [],
    leaves: null,
    assets: [],
    payslips: []
  });

  const employeeId = user?.employee_id || user?.linked_employee_id || user?.user_id;

  const loadData = async () => {
    if (!employeeId) {
      setError("User context not found. Please log in again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      const [empRes, attRes, leaveRes, assetRes, payRes] = await Promise.allSettled([
        employeeService.getEmployee(employeeId),
        employeeService.getAttendanceHistory(employeeId, currentMonth, currentYear),
        leaveService.getLeaveBalance(employeeId, currentYear),
        employeeService.getEmployeeAssets(employeeId),
        payrollService.getMyPayslips()
      ]);

      setData({
        profile: empRes.status === 'fulfilled' ? empRes.value.data : null,
        attendance: attRes.status === 'fulfilled' ? (attRes.value.data || []) : [],
        leaves: leaveRes.status === 'fulfilled' ? leaveRes.value.data : null,
        assets: assetRes.status === 'fulfilled' ? (assetRes.value.data || []) : [],
        payslips: payRes.status === 'fulfilled' ? (payRes.value.data || []) : []
      });
    } catch (err) {
      setError("An unexpected error occurred while syncing data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [employeeId]);

  // SAFE DOWNLOAD HANDLER: Checks if file exists before opening
  const handleSafeDownload = async (fileId, type) => {
    setDownloading(true);
    try {
      let response;
      if (type === 'payslip') {
        response = await payrollService.getPayslipBlob(fileId);
      } else {
        response = await employeeService.getDocBlob(employeeId, fileId); // fileId is 'contract' or 'appointment'
      }

      if (!response.ok) {
        alert("This document is not yet generated or available. Please contact HR.");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', (type === 'payslip' ? 'Payslip_' : 'Doc_') + fileId + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      alert("Network Error: Could not reach server.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="animate-spin text-primary w-12 h-12 opacity-20" />
    </div>
  );

  return (
    <div className="space-y-6 pb-20 text-gray-900">
      {/* Banner */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-primary/5 rounded-3xl flex items-center justify-center text-primary text-3xl font-black shadow-inner">
            {user?.full_name?.charAt(0) || 'U'}
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight tracking-widest uppercase">Self-Service Hub</h1>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mt-1">
              {user?.full_name} &bull; <span className="text-primary/70">{String(data.profile?.designation || 'Staff')}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-100 overflow-x-auto no-scrollbar bg-white rounded-t-xl px-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={'px-6 py-4 text-sm font-bold flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ' + 
              (activeTab === tab.id ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-gray-400 hover:text-gray-600')}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm font-bold flex items-center gap-2 border border-red-100">
          <AlertCircle className="w-4 h-4" /> {String(error)}
        </div>
      )}

      {/* Tab Content Area */}
      <div className="min-h-[300px] animate-in fade-in duration-500">
        
        {/* 1. MY PROFILE */}
        {activeTab === 'profile' && (
          !data.profile ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400 font-medium">Profile data is currently unavailable.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6 space-y-6">
                <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] border-b pb-4">Personal Details</h2>
                <div className="space-y-5">
                  <InfoRow icon={<Mail />} label="Corporate Email" value={String(data.profile.email)} />
                  <InfoRow icon={<Phone />} label="Phone Number" value={String(data.profile.phone)} />
                  <InfoRow icon={<Briefcase />} label="Department" value={String(data.profile.department)} />
                  <InfoRow icon={<Calendar />} label="Joining Date" value={String(data.profile.joining_date)} />
                </div>
              </div>
              <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6 space-y-6">
                <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] border-b pb-4">Job Information</h2>
                <div className="space-y-5">
                  <InfoRow icon={<FileText />} label="Identity Number" value={String(data.profile.cnic || 'N/A')} />
                  <InfoRow icon={<MapPin />} label="Job Type" value={String(data.profile.employment_type)} />
                  <InfoRow icon={<User />} label="Line Manager" value={String(data.profile.reporting_manager || 'CEO')} />
                </div>
              </div>
            </div>
          )
        )}

        {/* 2. ATTENDANCE */}
        {activeTab === 'attendance' && (
          <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
            <div className="p-4 bg-gray-50/50 border-b border-gray-100"><h2 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Monthly Logs</h2></div>
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest"><th className="px-6 py-4">Date</th><th className="px-6 py-4">Check-In / Out</th><th className="px-6 py-4">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.attendance.length === 0 ? (
                  <tr><td colSpan="3" className="p-12 text-center text-gray-400 italic">No records found for this period.</td></tr>
                ) : (
                  data.attendance.map((rec, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-700">{String(rec.date)}</td>
                      <td className="px-6 py-4 text-gray-500 font-medium font-mono">{String(rec.check_in || '—')} / {String(rec.check_out || '—')}</td>
                      <td className="px-6 py-4"><StatusBadge status={String(rec.status)} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 3. LEAVE BALANCE */}
        {activeTab === 'leaves' && (
          !data.leaves ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400">Balance records not found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(data.leaves.balances).map(([type, b]) => (
                <div key={type} className="bg-white p-6 rounded-card border border-gray-100 shadow-soft flex items-center justify-between group hover:border-primary/30 transition-all">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">{type} Quota</p>
                    <p className="text-2xl font-black text-gray-900 mt-1">{String(b.remaining)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-primary italic">Days Available</p>
                    <p className="text-[10px] font-bold text-gray-300 uppercase mt-1">Used: {String(b.used)}</p>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* 4. ASSIGNED ASSETS */}
        {activeTab === 'assets' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.assets.length === 0 ? (
              <div className="md:col-span-2 p-12 bg-white rounded-card border border-dashed text-center text-gray-400 font-medium italic">No assets assigned.</div>
            ) : (
              data.assets.map((a, i) => (
                <div key={i} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center gap-4 hover:border-primary/20 transition-all">
                  <div className="p-3 bg-primary/5 rounded-xl text-primary"><HardDrive className="w-6 h-6" /></div>
                  <div>
                    <h3 className="font-bold text-gray-900">{String(a.name)}</h3>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{String(a.category)} &bull; {String(a.asset_id)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 5. PAYSLIPS */}
        {activeTab === 'payroll' && (
          <div className="space-y-3">
            {data.payslips.length === 0 ? (
              <div className="p-12 bg-white rounded-card border border-dashed text-center text-gray-400">No payslips found.</div>
            ) : (
              data.payslips.map((p, i) => (
                <div key={i} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between hover:border-green-200 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-green-50 text-green-600 rounded-lg"><FileText className="w-5 h-5" /></div>
                    <div><h3 className="font-bold text-gray-900">Salary Slip - {String(p.month)}/{String(p.year)}</h3><p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Digital Copy</p></div>
                  </div>
                  <button 
                    disabled={downloading}
                    onClick={() => handleSafeDownload(p.payslip_id, 'payslip')} 
                    className="flex items-center gap-2 bg-gray-50 hover:bg-primary hover:text-white px-5 py-2 rounded-xl text-xs font-black uppercase transition-all border border-gray-100 shadow-sm"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download PDF
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* 6. MY DOCUMENTS (SAFE DOWNLOAD) */}
        {activeTab === 'documents' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DocumentCard 
               title="Appointment Letter" 
               desc="Official job offer and details"
               loading={downloading}
               onDownload={() => handleSafeDownload('appointment', 'doc')}
            />
            <DocumentCard 
               title="Employment Contract" 
               desc="Signed legal agreement"
               loading={downloading}
               onDownload={() => handleSafeDownload('contract', 'doc')}
            />
          </div>
        )}

      </div>
    </div>
  );
};

// Sub-components
const DocumentCard = ({ title, desc, onDownload, loading }) => (
  <div className="bg-white p-6 rounded-card border border-gray-100 shadow-soft flex items-center justify-between hover:border-primary/20 transition-all group">
    <div className="flex items-center gap-4">
      <div className="p-3 bg-gray-50 text-gray-400 group-hover:bg-primary/5 group-hover:text-primary rounded-2xl transition-all"><FileText className="w-7 h-7" /></div>
      <div><h3 className="font-bold text-gray-900">{title}</h3><p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5 tracking-tighter">{desc}</p></div>
    </div>
    <button disabled={loading} onClick={onDownload} className="p-3 bg-primary text-white rounded-xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
    </button>
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 group">
    <div className="p-2.5 bg-gray-50 text-primary/30 rounded-xl group-hover:bg-primary/5 group-hover:text-primary transition-all shadow-sm">
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest leading-none">{label}</p>
      <p className="text-sm font-bold text-gray-700 mt-2">{String(value || '—')}</p>
    </div>
  </div>
);

export default SelfService;